import type { ILogger } from '@microsoft/signalr';
import { LogLevel } from '@microsoft/signalr';

const ACCESS_TOKEN_QUERY_VALUE = /(access_token=)[^&\s"'<>]*/gi;

const LOG_LEVELS: Record<string, LogLevel> = {
  trace: LogLevel.Trace,
  debug: LogLevel.Debug,
  info: LogLevel.Information,
  information: LogLevel.Information,
  warn: LogLevel.Warning,
  warning: LogLevel.Warning,
  error: LogLevel.Error,
  critical: LogLevel.Critical,
  none: LogLevel.None,
};

export const redactSignalRLogMessage = (message: unknown): string =>
  String(message).replace(ACCESS_TOKEN_QUERY_VALUE, '$1[REDACTED]');

const resolveLogLevel = (configuredLevel?: string): LogLevel => {
  if (!configuredLevel) {
    return LogLevel.Error;
  }

  const level = LOG_LEVELS[configuredLevel.trim().toLowerCase()];
  if (level === undefined) {
    throw new Error(`Unknown SignalR log level: ${configuredLevel}`);
  }

  return level;
};

export const createSignalRLogger = (configuredLevel?: string): ILogger => {
  const minimumLogLevel = resolveLogLevel(configuredLevel);

  return {
    log(logLevel, message) {
      if (logLevel < minimumLogLevel || minimumLogLevel === LogLevel.None) {
        return;
      }

      const safeMessage = redactSignalRLogMessage(message);
      switch (logLevel) {
        case LogLevel.Critical:
        case LogLevel.Error:
          console.error(safeMessage);
          break;
        case LogLevel.Warning:
          console.warn(safeMessage);
          break;
        case LogLevel.Information:
          // SignalR's ILogger bridge intentionally writes informational output
          // to the browser console when explicitly enabled.
          // eslint-disable-next-line no-console
          console.info(safeMessage);
          break;
        default:
          // SignalR's ILogger bridge intentionally writes trace/debug output
          // to the browser console when explicitly enabled.
          // eslint-disable-next-line no-console
          console.log(safeMessage);
          break;
      }
    },
  };
};
